import { Injectable } from '@angular/core';
import { Observable, combineLatest, from, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { AuthService } from '@telespot/web-core';
import { Info, MicroService, SampleListItem } from '@analysis/infra/telespot';
import { CloudFunctions } from '@telespot/sdk';
import { NavigationStore } from '../../stores/navigation/navigation.store';

/**
 * Side effects feeding `NavigationStore`: navigation references for the
 * current sample, the breadcrumb dropdown options, and the decrypted-name
 * cache for encrypted workspaces. Component-provided alongside the store —
 * each analysis view gets an instance scoped to its sample.
 *
 * `fetchNavigationReferences` is used by every analysis view; the dropdown
 * option loading is opt-in via `loadBreadcrumbOptions()` so views without a
 * breadcrumb (microscope-video) don't pay for fetches they never render.
 */
@Injectable()
export class BreadcrumbNavigationEffects {
  private static readonly DECRYPTED_WS_KEY = 'telespot:decrypted_workspace';

  constructor(
    private readonly navigationStore: NavigationStore,
    private readonly microService: MicroService,
    private readonly authService: AuthService
  ) {}

  /** Pure-technician users only see cases they created. */
  private readonly technicianOnlyCreatorId: string | undefined = (() => {
    const roles = this.authService.currentUserRoleNames ?? [];
    const isTechnicianOnly = roles.length === 1 && roles[0] === 'technician';
    return isTechnicianOnly ? this.authService.currentUser?.id : undefined;
  })();

  /** Refresh the navigation references (current entities + prev/next) for a sample. */
  public readonly fetchNavigationReferences = this.navigationStore.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      switchMap((sampleId) =>
        from(this.microService.fetchNavigationReferences(sampleId, this.technicianOnlyCreatorId)).pipe(
          tap((navigationData) => this.navigationStore.updateNavigationReferences(navigationData)),
          catchError((error) => {
            this.navigationStore.setError(`Failed to fetch navigation references: ${error.message}`);
            return of(null);
          })
        )
      )
    )
  );

  // Breadcrumb dropdown option loaders. Cold pipelines subscribed once via
  // `loadBreadcrumbOptions`; each list resets to `null` (= loading row) and
  // refetches whenever the entity that scopes it changes. Workspaces are
  // scoped to the current workspace's organization (not every org the user
  // can see).
  private readonly workspaceOptionsLoader$ = this.navigationStore.currentWorkspace$.pipe(
    map((ws) => ws?.id),
    distinctUntilChanged(),
    tap(() => this.navigationStore.setWorkspaceOptions(null)),
    switchMap((workspaceId) =>
      workspaceId
        ? from(this.microService.listWorkspacesInSameOrg(workspaceId)).pipe(catchError(() => of([] as Info[])))
        : of([] as Info[])
    ),
    tap((options) => this.navigationStore.setWorkspaceOptions(options))
  );

  private readonly currentCasesLoader$ = this.navigationStore.currentWorkspace$.pipe(
    map((ws) => ws?.id),
    filter((workspaceId): workspaceId is string => !!workspaceId),
    distinctUntilChanged(),
    tap((workspaceId) => this.loadCasesForWorkspace(of(workspaceId)))
  );

  private readonly currentSamplesLoader$ = this.navigationStore.currentCase$.pipe(
    map((entry) => entry?.id),
    filter((caseId): caseId is string => !!caseId),
    distinctUntilChanged(),
    tap((caseId) => this.loadSamplesForCase(of(caseId)))
  );

  /**
   * Cases of ANY workspace, so the breadcrumb can cascade into one the user has not
   * opened. Already-loaded (or in-flight) parents are skipped, which is what makes it
   * safe to call on every hover.
   */
  public readonly loadCasesForWorkspace = this.navigationStore.effect((workspaceId$: Observable<string>) =>
    workspaceId$.pipe(
      filter((workspaceId) => !!workspaceId),
      concatLatestFrom(() => this.navigationStore.rawCasesByWorkspaceId$),
      filter(([workspaceId, byWorkspaceId]) => byWorkspaceId[workspaceId] === undefined),
      tap(([workspaceId]) => this.navigationStore.setCasesForWorkspace({ workspaceId, cases: null })),
      mergeMap(([workspaceId]) =>
        from(this.microService.listCasesInWorkspace(workspaceId, this.technicianOnlyCreatorId)).pipe(
          catchError(() => of([] as Info[])),
          tap((cases) => this.navigationStore.setCasesForWorkspace({ workspaceId, cases }))
        )
      )
    )
  );

  /** Samples of ANY case. Same skip-if-known rule as `loadCasesForWorkspace`. */
  public readonly loadSamplesForCase = this.navigationStore.effect((caseId$: Observable<string>) =>
    caseId$.pipe(
      filter((caseId) => !!caseId),
      concatLatestFrom(() => this.navigationStore.samplesByCaseId$),
      filter(([caseId, byCaseId]) => byCaseId[caseId] === undefined),
      tap(([caseId]) => this.navigationStore.setSamplesForCase({ caseId, samples: null })),
      mergeMap(([caseId]) =>
        from(this.microService.listSamplesInCase(caseId)).pipe(
          catchError(() => of([] as SampleListItem[])),
          tap((samples) => this.navigationStore.setSamplesForCase({ caseId, samples }))
        )
      )
    )
  );

  /**
   * Trigger decryption for the current case AND the dropdown options every
   * time either the current workspace or its case list changes. The cloud
   * function accepts a batch of ids, so the dropdown gets a single round-trip
   * instead of N. Already-cached ids are skipped to avoid re-fetching.
   */
  private readonly autoDecryptLoader$ = combineLatest([
    this.navigationStore.currentWorkspace$,
    this.navigationStore.currentCase$,
    this.navigationStore.rawCasesByWorkspaceId$.pipe(
      concatLatestFrom(() => this.navigationStore.currentWorkspace$),
      map(([byWorkspaceId, ws]) => (ws?.id ? byWorkspaceId[ws.id] ?? null : null))
    ),
  ]).pipe(
    filter(([ws]) => !!ws?.id),
    concatLatestFrom(() => this.navigationStore.decryptedNamesByCaseId$),
    switchMap(([[ws, currentCase, options], cached]) => {
      const decryptedWsId = sessionStorage.getItem(BreadcrumbNavigationEffects.DECRYPTED_WS_KEY);
      if (decryptedWsId !== ws.id) return of(null);

      const ids = new Set<string>();
      if (currentCase?.id && !cached[currentCase.id]) ids.add(currentCase.id);
      for (const opt of options ?? []) {
        if (opt?.id && !cached[opt.id]) ids.add(opt.id);
      }
      if (ids.size === 0) return of(null);

      return from(CloudFunctions.decryptCases(Array.from(ids), false)).pipe(
        catchError(() => of([] as Array<{ id: string; name: string }>))
      );
    }),
    tap((dtos) => {
      if (!Array.isArray(dtos) || dtos.length === 0) return;
      const next: Record<string, string> = {};
      for (const dto of dtos) {
        if (dto?.id && dto?.name) next[dto.id] = dto.name;
      }
      if (Object.keys(next).length > 0) this.navigationStore.mergeDecryptedNames(next);
    })
  );

  /**
   * One-shot opt-in: starts the reactive loading of the breadcrumb dropdown
   * options (workspaces, cases, samples) and the auto-decrypt of case names.
   * Views that render the breadcrumb call this once from their facade;
   * repeated calls are ignored.
   */
  public readonly loadBreadcrumbOptions = this.navigationStore.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      take(1),
      switchMap(() =>
        merge(
          this.workspaceOptionsLoader$,
          this.currentCasesLoader$,
          this.currentSamplesLoader$,
          this.autoDecryptLoader$
        )
      )
    )
  );

}
